import { Clock } from "lucide-react";
import { cn } from "src/lib/utils";

interface QuizTimerProps {
  remainingSeconds: number;
  compact?: boolean;
}

export function QuizTimer({ remainingSeconds, compact = false }: QuizTimerProps) {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const label = `${minutes}:${String(seconds).padStart(2, "0")}`;
  const announcement = remainingSeconds === 300
    ? "5 minutes remaining"
    : remainingSeconds === 60
    ? "1 minute remaining"
    : "";

  if (compact) {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-lg border px-2.5 py-1 font-mono text-xs sm:text-sm font-bold tabular-nums shadow-2xs transition-colors",
          remainingSeconds <= 60
            ? "border-red-200 bg-red-50 text-red-700 animate-pulse"
            : remainingSeconds <= 300
            ? "border-amber-200 bg-amber-50 text-amber-800"
            : "border-slate-200 bg-white text-slate-900"
        )}
        title="Time Remaining"
      >
        <Clock className="h-3.5 w-3.5 text-purple-600 shrink-0" aria-hidden="true" />
        <span aria-hidden="true">{label}</span>
        <span className="sr-only">{minutes} minutes {seconds} seconds remaining</span>
        <div aria-live="assertive" className="sr-only">{announcement}</div>
      </div>
    );
  }

  return (
    <div className="text-right">
      <p className="text-xs uppercase tracking-wide text-gray-500">Time left</p>
      <p className={`font-mono text-2xl font-bold tabular-nums ${remainingSeconds <= 60 ? "text-red-700" : "text-purple-950"}`}>
        <span aria-hidden="true">{label}</span>
        <span className="sr-only">{minutes} minutes {seconds} seconds remaining</span>
      </p>
      <div aria-live="assertive" className="sr-only">{announcement}</div>
    </div>
  );
}

export default QuizTimer;
